import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para PUERTAS CORTAFUEGOS.
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II.
 *   - UNE-EN 1634-1 (resistencia al fuego de puertas).
 *   - UNE-EN 1125 / EN 179 (dispositivos antipánico / manilla).
 *   - UNE-EN 1154 (dispositivos de cierre controlado).
 *   - UNE-EN 1155 (retenedores electromagnéticos).
 *   - CTE DB-SI.
 *
 * Periodicidad: T = Trimestral · A = Anual.
 */
export const PUERTAS_CORTAFUEGOS_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'pci_puertas_cortafuegos',
  titulo: 'Puertas Cortafuegos',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y configuración general',
      cabecera: [
        { key: 'num_puertas', label: 'Nº total de puertas cortafuegos', tipo: 'number' },
        { key: 'sectores', label: 'Nº de sectores de incendio' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿El inventario físico coincide con planos de sectorización?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿La clasificación EI de cada puerta es coherente con el sector que delimita (CTE DB-SI)?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿Existe identificación / numeración visible en cada puerta?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'puerta',
      titulo: '2. Puerta cortafuegos · estado',
      cabecera: [
        { key: 'planta', label: 'Planta' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'numero_serie', label: 'Nº Serie' },
        { key: 'clasificacion_ei', label: 'Clasificación EI' },
        { key: 'anchura_mm', label: 'Anchura (mm)' },
      ],
      preguntas: [
        { id: 'PT.01', texto: '¿La puerta dispone de placa identificativa visible (marca, modelo, EI, marcado CE)?', periodicidad: ['A'], unidades: true },
        { id: 'PT.02', texto: '¿La hoja está libre de deformaciones, abolladuras, perforaciones u oxidación?', periodicidad: ['T','A'], unidades: true },
        { id: 'PT.03', texto: '¿Las juntas intumescentes / de humo están completas, continuas y sin daños?', periodicidad: ['T','A'], unidades: true },
        { id: 'PT.04', texto: '¿El marco está correctamente fijado al paramento y sin huecos perimetrales?', periodicidad: ['A'], unidades: true },
        { id: 'PT.05', texto: '¿Las bisagras están engrasadas y sin holguras; tornillería completa?', periodicidad: ['T','A'], unidades: true },
        { id: 'PT.06', texto: '¿La puerta está libre de cuñas, ataduras u objetos que impidan su cierre?', periodicidad: ['T','A'], unidades: true },
        { id: 'PT.07', texto: '¿La señalización ("Puerta cortafuegos - manténgase cerrada") es visible y legible?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'cierre',
      titulo: '3. Dispositivo de cierre (cierrapuertas)',
      preguntas: [
        { id: 'CI.01', texto: 'Si dispone de cierrapuertas: ¿la puerta cierra completamente desde cualquier ángulo de apertura?', periodicidad: ['T','A'], unidades: true },
        { id: 'CI.02', texto: '¿La velocidad de cierre y golpe final están correctamente regulados (UNE-EN 1154)?', periodicidad: ['T','A'], unidades: true },
        { id: 'CI.03', texto: '¿El cierrapuertas está libre de fugas de aceite y firmemente anclado?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'antipanico',
      titulo: '4. Barra antipánico / cerradura',
      preguntas: [
        { id: 'AP.01', texto: 'Si dispone de barra antipánico: ¿acciona la apertura con un empuje ≤ 80 N (UNE-EN 1125)?', periodicidad: ['T','A'], unidades: true },
        { id: 'AP.02', texto: '¿La cerradura / pestillo encaja correctamente en el cerradero del marco?', periodicidad: ['T','A'], unidades: true },
        { id: 'AP.03', texto: '¿La maniobra desde el lado de evacuación funciona sin llave ni herramientas?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'selector',
      titulo: '5. Selector de cierre (puertas de dos hojas)',
      preguntas: [
        { id: 'SE.01', texto: 'En puertas de dos hojas: ¿el selector garantiza el orden correcto de cierre?', periodicidad: ['T','A'], unidades: true },
        { id: 'SE.02', texto: '¿Ambas hojas cierran completamente sin dejar luz entre ellas ni con el marco?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'retenedor',
      titulo: '6. Retenedor electromagnético',
      preguntas: [
        { id: 'RT.01', texto: 'Si dispone de retenedor: ¿mantiene la puerta abierta de forma estable?', periodicidad: ['T','A'], unidades: true },
        { id: 'RT.02', texto: '¿La puerta se libera y cierra completamente al activarse la alarma de incendios?', periodicidad: ['T','A'], unidades: true },
        { id: 'RT.03', texto: '¿El retenedor libera también ante corte de alimentación (fallo seguro)?', periodicidad: ['A'], unidades: true },
        { id: 'RT.04', texto: '¿El cableado y la fijación del retenedor están en buen estado?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'documentacion',
      titulo: '7. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Está actualizado el inventario con marca, modelo, EI, ubicación y Nº de serie?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Se conservan los certificados de ensayo / declaración de prestaciones (DoP) de las puertas?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe de anomalías y acciones correctoras?', periodicidad: ['A'] },
      ],
    },
  ],
};
